import * as Sentry from '@sentry/react';
import { SeverityLevel } from '@sentry/react';
import { getEnvironment } from '@app/common';

const getReleaseIdentifier = () => {
  const shortRev = process?.env?.BITBUCKET_COMMIT?.substring(0, 10);
  const buildNumber = process?.env?.BITBUCKET_BUILD_NUMBER;

  if (shortRev && buildNumber) {
    return `${buildNumber}-${shortRev}`;
  }
  if (buildNumber) {
    return buildNumber;
  }
  if (shortRev) {
    return shortRev;
  }
  // Return a stable fallback when CI env vars are absent (e.g. local dev / test)
  // so the release string never contains a literal "undefined".
  return 'local';
};

// https://atlassian-2y.sentry.io/insights/projects/public-app-updates/?project=4511072889864192
export const sentryConfig = {
  dsn: 'https://5b81abb385f760bd950717459f42c2d3@o55978.ingest.us.sentry.io/4511072889864192',
  integrations: [
    Sentry.browserTracingIntegration(),
    Sentry.replayIntegration(),
  ],
  release: `app-updates-frontend@1.0.0-build.${getReleaseIdentifier()}`,
  // Tracing
  tracesSampleRate: 0.01,
  // Session Replay
  replaysSessionSampleRate: 0.01,
  replaysOnErrorSampleRate: 1.0,
};

const captureMessage = (message: string, severityLevel: SeverityLevel) => {
  if (Sentry.isInitialized()) {
    Sentry.captureMessage(message, severityLevel);
  }
};

/**
 * Returns the tracing and session replay sample rates for the given environment.
 * - prod: full sample rates from config
 * - staging: half of prod rates (all three rates are halved)
 * - development: zero — Sentry is initialized but must not send any data
 *
 * @param config - Optional partial config override. Only the three sample-rate
 *   fields are read; any omitted fields fall back to the corresponding
 *   `sentryConfig` defaults. When called from `initSentry` the fully-merged
 *   `resolvedConfig` is passed, so overrides are always respected.
 */
export const getSampleRates = (
  environment: ReturnType<typeof getEnvironment>,
  config: Partial<
    Pick<
      typeof sentryConfig,
      | 'tracesSampleRate'
      | 'replaysSessionSampleRate'
      | 'replaysOnErrorSampleRate'
    >
  > = sentryConfig,
) => {
  if (environment === 'development') {
    return {
      tracesSampleRate: 0,
      replaysSessionSampleRate: 0,
      replaysOnErrorSampleRate: 0,
    };
  }
  if (environment === 'staging') {
    return {
      // All sample rates are halved for staging, including replay-on-error
      tracesSampleRate:
        (config.tracesSampleRate ?? sentryConfig.tracesSampleRate) / 2,
      replaysSessionSampleRate:
        (config.replaysSessionSampleRate ??
          sentryConfig.replaysSessionSampleRate) / 2,
      replaysOnErrorSampleRate:
        (config.replaysOnErrorSampleRate ??
          sentryConfig.replaysOnErrorSampleRate) / 2,
    };
  }
  // Prod: use the config values directly, falling back to sentryConfig defaults
  // if an explicit `undefined` was passed in a partial override.
  return {
    tracesSampleRate: config.tracesSampleRate ?? sentryConfig.tracesSampleRate,
    replaysSessionSampleRate:
      config.replaysSessionSampleRate ?? sentryConfig.replaysSessionSampleRate,
    replaysOnErrorSampleRate:
      config.replaysOnErrorSampleRate ?? sentryConfig.replaysOnErrorSampleRate,
  };
};

/**
 * Initializes Sentry with the provided configuration.
 * Sentry is initialized in all environments so it can be tested locally.
 * In development, all sample rates are set to 0 and beforeSend hooks drop
 * every event so nothing is transmitted to Sentry.
 * Sample rates are halved for staging compared to prod.
 *
 * @param config - Optional partial config override. Defaults to `sentryConfig`.
 *   Pass a custom config in tests to avoid mutating the exported `sentryConfig` object.
 */
export const initSentry = (config: Partial<typeof sentryConfig> = {}) => {
  const environment = getEnvironment();
  const resolvedConfig = { ...sentryConfig, ...config };

  if (resolvedConfig.dsn) {
    Sentry.init({
      ...resolvedConfig,
      environment,
      ...getSampleRates(environment, resolvedConfig),
      // In development, drop all events to prevent any data from being sent
      ...(environment === 'development' && {
        beforeSend: () => null,
        beforeSendTransaction: () => null,
      }),
    });
  }
};

/**
 * Sentry Logger singleton for error tracking and reporting
 *
 * Example usage:
 *
 * import SentryLogger from '@app/sentry';
 * const error = new Error('Something terrible has happened');
 * SentryLogger.error(error, {
 *   extra: {
 *     userId: '123',
 *     action: 'button-click',
 *   },
 * });
 */
export default {
  info: (message: string) => {
    captureMessage(message, 'info');
  },
  warn: (message: string) => {
    captureMessage(message, 'warning');
  },
  debug: (message: string) => {
    captureMessage(message, 'debug');
  },
  error: (exception: any, ...args: any[]) => {
    if (Sentry.isInitialized()) {
      Sentry.captureException(exception, ...args);
    }
  },
};

// Re-export Sentry for advanced usage
export { Sentry };
